import { CheckCircle, Robot, XCircle } from '@phosphor-icons/react';
import {
  getGetAgentConnectionRequestQueryKey,
  useApproveAgentConnection,
  useDeclineAgentConnection,
  useGetAgentConnectionRequest,
} from '@reelplicate/api-client/agent-access';
import type {
  AgentConnectionRequestDto,
  WorkspaceRole,
} from '@reelplicate/api-client/model';
import {
  BrandMark,
  Button,
  Field,
  Select,
  SelectItem,
  Skeleton,
  Stack,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';

import { AuthBody, AuthCard, AuthHeader } from '@/features/auth/auth-card';
import { AGENT_ROLES, agentMark } from '@/features/settings/agents';
import { getErrorMessage } from '@/lib/errors';

function AgentTile({ name }: { name: string }) {
  const mark = agentMark(name);
  return (
    <span
      aria-hidden
      className="bg-bg-surface border-border-subtle shadow-sm flex size-14 items-center justify-center rounded-xl border"
    >
      {mark ? (
        <BrandMark name={mark} size={28} />
      ) : (
        <Robot className="text-icon-primary size-7" />
      )}
    </span>
  );
}

function SettingsButton() {
  return (
    <Button asChild variant="outline" size="lg" className="w-full">
      <RouterLink to="/settings/api">See connected agents</RouterLink>
    </Button>
  );
}

function ConnectSkeleton() {
  return (
    <AuthCard>
      <Stack gap={4} align="center" aria-busy="true" aria-label="Loading">
        <Skeleton shape="rectangle" shimmer className="size-14 rounded-xl" />
        <Skeleton className="h-7 w-48" />
        <Skeleton className="w-64" />
        <Skeleton shape="rectangle" shimmer className="h-12 w-full" />
      </Stack>
    </AuthCard>
  );
}

/**
 * Where an agent (an MCP client) sends its user after they sign in through
 * OAuth: pick the workspace it may work in and the role it gets, then
 * approve. Until then every tool it calls answers with this page's link.
 */
export function ConnectAgentPage() {
  const { connectionId = '' } = useParams();
  const request = useGetAgentConnectionRequest(connectionId, {
    query: { retry: false, throwOnError: false },
  });

  if (isAxiosError(request.error) && request.error.response?.status === 404) {
    return (
      <AuthCard>
        <Well tone="danger" title="This link isn’t valid">
          It may be for a different account. Sign in as the person who connected the
          agent, or connect it again.
        </Well>
        <SettingsButton />
      </AuthCard>
    );
  }
  if (request.error) {
    return (
      <AuthCard>
        <Well tone="danger" title="Couldn’t load this request">
          {getErrorMessage(request.error)}
        </Well>
        <Button size="lg" className="w-full" onClick={() => void request.refetch()}>
          Try again
        </Button>
      </AuthCard>
    );
  }
  if (!request.data) return <ConnectSkeleton />;

  return <ConnectAgent request={request.data} />;
}

function ConnectAgent({ request }: { request: AgentConnectionRequestDto }) {
  const queryClient = useQueryClient();
  const approve = useApproveAgentConnection();
  const decline = useDeclineAgentConnection();
  const [workspaceId, setWorkspaceId] = useState(request.workspaces[0]?.id ?? '');
  const workspace = request.workspaces.find((w) => w.id === workspaceId);
  const roles = workspace?.grantableRoles ?? [];
  const [role, setRole] = useState<WorkspaceRole | undefined>(undefined);
  const chosenRole =
    role && roles.includes(role) ? role : roles.includes('EDITOR') ? 'EDITOR' : roles[0];
  const { agentName } = request;

  const refresh = (data: AgentConnectionRequestDto) =>
    queryClient.setQueryData(getGetAgentConnectionRequestQueryKey(request.id), data);

  const onApprove = async () => {
    if (!chosenRole) return;
    try {
      refresh(
        await approve.mutateAsync({
          connectionId: request.id,
          data: { workspaceId, role: chosenRole },
        }),
      );
    } catch {
      // The failure is already shown as a toast.
    }
  };

  const onDecline = async () => {
    try {
      await decline.mutateAsync({ connectionId: request.id });
    } catch {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: getGetAgentConnectionRequestQueryKey(request.id),
    });
  };

  if (request.status === 'ACTIVE') {
    return (
      <AuthCard>
        <AuthHeader
          icon={{ icon: CheckCircle, tone: 'success' }}
          title={`${agentName} is connected`}
          subtitle={`It works in ${request.workspaceName ?? 'your workspace'} as ${AGENT_ROLES[request.role ?? 'EDITOR'].name.toLowerCase()}. Go back to ${agentName} and try again.`}
        />
        <AuthBody>
          <SettingsButton />
        </AuthBody>
      </AuthCard>
    );
  }

  if (request.status === 'REVOKED') {
    return (
      <AuthCard>
        <AuthHeader
          icon={{ icon: XCircle, tone: 'danger' }}
          title={`${agentName} isn’t connected`}
          subtitle={`This request was turned down or revoked. Connect ${agentName} again to start over.`}
        />
        <AuthBody>
          <SettingsButton />
        </AuthBody>
      </AuthCard>
    );
  }

  if (request.workspaces.length === 0) {
    return (
      <AuthCard>
        <Well tone="warning" title="You’re not in a workspace yet">
          Finish setting up Reelplicate, then connect {agentName} again.
        </Well>
        <Button asChild size="lg" className="w-full">
          <RouterLink to="/">Go to Reelplicate</RouterLink>
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <Stack gap={4} align="center">
        <AgentTile name={agentName} />
        <AuthHeader
          title={`Connect ${agentName}`}
          subtitle={`${agentName} will act as you, limited to the role you choose. You can revoke it any time in Settings → API & MCP.`}
        />
      </Stack>
      <AuthBody>
        {request.workspaces.length > 1 && (
          <Field label="Workspace">
            <Select value={workspaceId} onValueChange={setWorkspaceId}>
              {request.workspaces.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </Select>
          </Field>
        )}
        {chosenRole && (
          <Field
            label="Role"
            helper={
              roles.length > 1
                ? AGENT_ROLES[chosenRole].description
                : `${AGENT_ROLES[chosenRole].description} An agent can’t have more access than you.`
            }
          >
            <Select
              value={chosenRole}
              onValueChange={(value) => setRole(value as WorkspaceRole)}
              disabled={roles.length < 2}
            >
              {roles.map((option) => (
                <SelectItem key={option} value={option}>
                  {AGENT_ROLES[option].name}
                </SelectItem>
              ))}
            </Select>
          </Field>
        )}
        <Button
          size="lg"
          className="w-full"
          loading={approve.isPending}
          disabled={!chosenRole}
          onClick={() => void onApprove()}
        >
          Approve
        </Button>
        <Button
          variant="ghost"
          size="lg"
          className="w-full"
          loading={decline.isPending}
          onClick={() => void onDecline()}
        >
          Don’t allow
        </Button>
      </AuthBody>
    </AuthCard>
  );
}
